import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import Icon from '../AppIcon';

const NotificationBell = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead, loading } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(e?.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const typeIcon = (type) => {
    switch (type) {
      case 'success': return { icon: 'CheckCircle', color: 'text-green-500' };
      case 'error': return { icon: 'XCircle', color: 'text-red-500' };
      case 'warning': return { icon: 'AlertTriangle', color: 'text-yellow-500' };
      default: return { icon: 'Bell', color: 'text-blue-500' };
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr)?.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return new Date(dateStr)?.toLocaleDateString();
  };

  const handleNotificationClick = async (n) => {
    if (!n?.is_read) {
      await markAsRead(n?.id);
    }
    setIsOpen(false);
    // Navigate to reservation-request with highlight param if related to reservation_requests
    if (n?.related_table === 'reservation_requests' && n?.related_id) {
      navigate(`/reservation-request?highlight=${n?.related_id}`);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center justify-center w-9 h-9 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
      >
        <Icon name="Bell" size={20} className="text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-11 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900 text-sm">Notifications</span>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs font-semibold rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-primary hover:underline font-medium"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Icon name="Loader2" size={18} className="animate-spin text-gray-400" />
              </div>
            ) : notifications?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <Icon name="BellOff" size={28} className="mb-2 opacity-50" />
                <span className="text-sm">No notifications yet</span>
              </div>
            ) : (
              notifications?.map((n) => {
                const { icon, color } = typeIcon(n?.type);
                const isClickable = n?.related_table === 'reservation_requests' && n?.related_id;
                return (
                  <div
                    key={n?.id}
                    onClick={() => handleNotificationClick(n)}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 transition-colors hover:bg-gray-50 ${
                      !n?.is_read ? 'bg-blue-50/40' : ''
                    } ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    <div className={`mt-0.5 flex-shrink-0 ${color}`}>
                      <Icon name={icon} size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium text-gray-900 leading-tight ${!n?.is_read ? 'font-semibold' : ''}`}>
                          {n?.title}
                        </p>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {isClickable && (
                            <Icon name="ExternalLink" size={11} className="text-gray-400" />
                          )}
                          {!n?.is_read && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full" />
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 leading-snug">{n?.message}</p>
                      <p className="text-xs text-gray-400 mt-1">{formatTime(n?.created_at)}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
